import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { BOOKING_METHODS } from 'features/one_off_modal';
import { ContactDetailRows } from './contact_detail_rows';
import { InviteeActions } from './invitee_actions';
import { getPaymentInfo } from './payment_utils';
import { InfoContainer as Container, expandableInviteeDetailsTopGapCss, inviteeDetailsInCardsStackCss } from './styles';
import { Question, QuestionsSection, MAX_VISIBLE_QUESTIONS, QuestionsList, ShowMoreToggle, OverflowQuestions } from '../questions_widget';
const detailsContainerCss = "d1ce7dn6";
const contactRowsIndentCss = "c1g9rtdz";
const attendeeActionsRowCss = "a58nobq";
const RescheduledBanner = ({
  cancellation
}) => <ContextualBanner variant="standard">
    <BodyText size="medium">
      <strong>
        {I18n.t('publisher.meetings.invitees.rescheduled_by', {
        actor: cancellation.cancelledBy
      })}
      </strong>
    </BodyText>
    <BodyText size="medium">
      {cancellation.reason?.replace(I18n.t('publisher.dashboard.event.rescheduled_prefix'), '')}
    </BodyText>
  </ContextualBanner>;
const CancelledBanner = ({
  cancellation
}) => <ContextualBanner variant="danger">
    <BodyText size="medium">
      <strong>{cancellation.attribution}</strong>
    </BodyText>
    <BodyText size="medium">
      {cancellation.reason}
    </BodyText>
  </ContextualBanner>;
const questionsCardCss = "qjzruy5";
const QuestionsCard = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(questionsCardCss, props.className)} />;
});
const InviteeQuestions = ({
  invitee
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const routingQuestions = invitee.formBuilderSubmission?.questions;
  const attendeeQuestions = invitee.eventFields;
  const hasRouting = routingQuestions?.length > 0;
  const hasAttendee = attendeeQuestions?.length > 0;
  if (!hasRouting && !hasAttendee) return null;
  const routingOverflow = (routingQuestions?.length ?? 0) > MAX_VISIBLE_QUESTIONS;
  const attendeeOverflow = (attendeeQuestions?.length ?? 0) > MAX_VISIBLE_QUESTIONS;
  const hasOverflow = routingOverflow || attendeeOverflow;
  return <QuestionsCard data-testid="invitee-questions">
      <QuestionsList>
        <BodyText weight="bold">{I18n.t('publisher.meetings.tabs.questions')}</BodyText>
        {hasRouting ? <>
            <QuestionsSection label={I18n.t('publisher.meeting_intelligence.details.questions.routing_label')} questions={routingQuestions} maxVisible={MAX_VISIBLE_QUESTIONS} />
            <OverflowQuestions questions={routingQuestions} isExpanded={isExpanded} />
            <QuestionsSection label={I18n.t('publisher.meeting_intelligence.details.questions.attendee_label')} questions={attendeeQuestions} maxVisible={MAX_VISIBLE_QUESTIONS} />
          </> : attendeeQuestions.slice(0, MAX_VISIBLE_QUESTIONS).map(question => <Question key={question.name} {...question} />)}
        <OverflowQuestions questions={attendeeQuestions} isExpanded={isExpanded} />
        {hasOverflow && <ShowMoreToggle isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />}
      </QuestionsList>
    </QuestionsCard>;
};
export const InviteeDetails = ({
  event,
  invitee,
  readonly,
  openEditEmailModal,
  canMarkNoShow,
  turnOnNoShowClicked,
  canReschedule: canRescheduleProp,
  canCancel: canCancelProp,
  canEditEmail: canEditEmailProp,
  canBlock,
  canUnblock,
  contactInfo = {},
  sendAnalytics,
  afterExpandableHeader = false,
  className,
  packageInfo,
  viewPackage
}) => {
  const isCRMContactCardFlagEnabled = useFlag('crm_contactcard_feature');
  const isCRMContactDetailFlagEnabled = useFlag('crm_contactdetails_feature');
  const isCRMContactDetailsPageEnabled = isCRMContactCardFlagEnabled && isCRMContactDetailFlagEnabled;
  const contactUuid = contactInfo.contactUuid ?? null;
  const contactId = contactInfo.contactId ?? null;
  const resolvedEmail = contactInfo.email ?? invitee.email ?? '';
  const resolvedName = contactInfo.name ?? invitee.name ?? '';
  const resolvedForActions = contactUuid || contactId ? {
    contactUuid,
    contactId,
    email: resolvedEmail,
    name: resolvedName
  } : undefined;
  const isPoll = event.eventType.bookingMethod === BOOKING_METHODS.poll;
  const {
    email,
    phoneNumber: inviteePhone,
    blockedByHost,
    standardTimezoneTitle,
    timezone,
    cancelled,
    cancellation,
    newSchedulable,
    charge,
    policy
  } = invitee;
  const isGuest = !invitee.id && !invitee.uuid;
  const isEventCancelled = event.cancelled;
  const canReschedule = canRescheduleProp ?? (!readonly && policy?.reschedule && !isPoll);
  const canCancel = canCancelProp ?? (!readonly && policy?.cancel);
  const canEditEmail = canEditEmailProp ?? (!readonly && !isGuest && !isEventCancelled && policy?.update);
  const isCancelled = cancelled && !newSchedulable;
  const isRescheduled = cancelled && newSchedulable;
  const hasCharge = charge && charge.captured;
  const paymentInfo = getPaymentInfo(charge, hasCharge);

  // Match InviteeActions: email uses contactUuid; View full profile uses contactId only when CRM page flags are on.
  const hasContactPrimaryAction = !!contactUuid || !!contactId && isCRMContactDetailsPageEnabled;
  const showInviteeActionsRow = canMarkNoShow && !blockedByHost || hasContactPrimaryAction || canReschedule || canCancel || canEditEmail || canBlock || canUnblock;
  return <Container className={classNames(detailsContainerCss, inviteeDetailsInCardsStackCss, afterExpandableHeader && expandableInviteeDetailsTopGapCss, className)}>
      {isCancelled && <CancelledBanner cancellation={cancellation} />}
      {isRescheduled && <RescheduledBanner cancellation={cancellation} />}
      <div className={contactRowsIndentCss}>
        <ContactDetailRows email={email} phoneNumber={contactInfo.phone || inviteePhone} timezone={contactInfo.timezone || standardTimezoneTitle || timezone} paymentInfo={hasCharge ? paymentInfo : undefined} paymentLink={hasCharge && charge?.externalChargeLink ? charge.externalChargeLink : undefined} packageInfo={packageInfo} viewPackage={viewPackage} />
      </div>
      {showInviteeActionsRow && <div className={attendeeActionsRowCss} data-testid="invitee-actions-row">
        <InviteeActions event={event} invitee={invitee} resolved={resolvedForActions} canMarkNoShow={canMarkNoShow} onMarkAsNoShow={turnOnNoShowClicked} sendAnalytics={sendAnalytics} showPrimaryActions canReschedule={canReschedule} canCancel={canCancel} canBlock={canBlock} canUnblock={canUnblock} canEditEmail={canEditEmail} openEditEmailModal={openEditEmailModal} />
      </div>}
      <InviteeQuestions invitee={invitee} />
    </Container>;
};

require("./invitee_details.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitee_details.js");